import type { SsoProtocol } from '@libs/enums';
import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import { Characters, ItemName, Locale, UploadFile } from '@ps-smart-cp/ppfw-client';

interface Schema {
  protocol: SsoProtocol;
  metadataXml: File | null;
  clientId: string;
  clientSecret: string;
  issuerUrl: string;
}

interface Record {
  protocol: string;
  metadataXml: string;
  clientId: string;
  clientSecret: string;
  issuerUrl: string;
}

const attributes = {
  metadataXml: UploadFile.new(true),
  clientId: Characters.preset.freeFormat({ maxLen: 128, required: true }),
  clientSecret: Characters.preset.freeFormat({ maxLen: 512, required: true }),
  issuerUrl: Characters.preset.freeFormat({ maxLen: 2048, required: true }),
} satisfies ItemAttributes<Schema>;

const names = {
  metadataXml: ItemName.of([Locale.ja, 'Metadata XML'], [Locale.en, 'Metadata XML']),
  clientId: ItemName.of([Locale.ja, 'クライアントID'], [Locale.en, 'Client ID']),
  clientSecret: ItemName.of([Locale.ja, 'クライアントシークレット'], [Locale.en, 'Client Secret']),
  issuerUrl: ItemName.of([Locale.ja, '発行者URL'], [Locale.en, 'Issuer URL']),
} satisfies ItemNames<Schema>;

const titleNames = {
  ssoSetting: ItemName.of([Locale.ja, 'SSO設定'], [Locale.en, 'SSO Setting']),
  ssoDisableDialog: ItemName.of([Locale.ja, '無効化確認'], [Locale.en, 'Confirmation of disablement']),
  ssoEnableDialog: ItemName.of([Locale.ja, '有効化確認'], [Locale.en, 'Confirmation of enablement']),
  ssoRequiredDisableDialog: ItemName.of(
    [Locale.ja, 'SSO強制の無効化確認'],
    [Locale.en, 'Confirmation of forced SSO disablement'],
  ),
  ssoRequiredEnableDialog: ItemName.of(
    [Locale.ja, 'SSO強制の有効化確認'],
    [Locale.en, 'Confirmation of forced SSO enablement'],
  ),
};

const labelNames = {
  oidcUnset: ItemName.of([Locale.ja, 'OIDC SSOは未設定です。'], [Locale.en, 'OIDC SSO is not configured.']),
  oidcEnable: ItemName.of(
    [Locale.ja, 'OIDC SSOは有効です。無効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'OIDC SSO is enabled. To disable, click the checkbox.'],
  ),
  oidcDisable: ItemName.of(
    [Locale.ja, 'OIDC SSOは無効です。有効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'OIDC SSO is disabled. To enable, click the checkbox.'],
  ),
  samlUnset: ItemName.of([Locale.ja, 'SAML SSOは未設定です。'], [Locale.en, 'SAML SSO is not configured.']),
  samlEnable: ItemName.of(
    [Locale.ja, 'SAML SSOは有効です。無効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'SAML SSO is enabled. To disable, click the checkbox.'],
  ),
  samlDisable: ItemName.of(
    [Locale.ja, 'SAML SSOは無効です。再度有効化するにはチェックボックスをクリックしてください。'],
    [Locale.en, 'SAML SSO is disabled. Click the checkbox to re-enable.'],
  ),
  ssoRequired: ItemName.of([Locale.ja, 'ログインユーザにSSOを強制する'], [Locale.en, 'Force SSO']),
  confirmDisableSso: ItemName.of(
    [Locale.ja, '無効化します。よろしいですか？'],
    [Locale.en, 'Disable. May I?'],
  ),
  confirmEnableSso: ItemName.of([Locale.ja, '有効化します。よろしいですか？'], [Locale.en, 'Enable. May I?']),
  confirmDisableSsoRequired: ItemName.of(
    [Locale.ja, 'SSO強制を無効化します。よろしいですか？'],
    [Locale.en, 'Disable forced SSO. May I?'],
  ),
  confirmEnableSsoRequired: ItemName.of(
    [Locale.ja, 'SSO強制を有効化します。よろしいですか？'],
    [Locale.en, 'Enable forced SSO. May I?'],
  ),
};

const ssoSetting = {
  attributes,
  names,
  labelNames,
  titleNames,
};

interface SsoSetting {
  schema: Schema;
  record: Record;
}

export type { SsoSetting };
export { ssoSetting };
